import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { formatClock, plainText, queryTracks } from "./catalog";
import { usePodcast } from "./PlayerContext";
import "./podcast.css";

function Cover({ track }) {
  const src = track?.posterUrl || track?.albumCoverUrl;
  if (src) return <img src={src} alt="" />;
  return (
    <span className="is-empty">
      <Icon name="microphone" />
    </span>
  );
}

export default function PodcastNow() {
  const podcast = usePodcast();
  const track = podcast.current;
  const [panel, setPanel] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);
  const saved = track ? podcast.favorites.includes(track._id) : false;
  const myRating = track ? podcast.ratings[track._id] : 0;

  useEffect(() => {
    const video = podcast.videoRef.current;
    const stage = document.getElementById("pod-stage");
    if (!podcast.videoMode || !video || !stage) return undefined;
    const place = () => {
      const rect = stage.getBoundingClientRect();
      video.style.position = "fixed";
      video.style.top = `${rect.top}px`;
      video.style.left = `${rect.left}px`;
      video.style.width = `${rect.width}px`;
      video.style.height = `${rect.height}px`;
      video.style.opacity = "1";
      video.style.zIndex = "30";
      video.style.objectFit = "cover";
      video.style.borderRadius = "10px";
      video.style.pointerEvents = "auto";
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      video.style.cssText = "";
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [podcast.videoMode, podcast.videoRef, track]);
  const related = useMemo(() => {
    if (!track) return [];
    const sameAlbum = track.albumId
      ? queryTracks(podcast.tracks, { albumId: track.albumId })
      : [];
    const sameTopic = queryTracks(podcast.tracks, { mood: track.mood });
    const pool = (sameAlbum.length > 1 ? sameAlbum : sameTopic).filter((item) => item._id !== track._id);
    return pool.slice(0, 8);
  }, [podcast.tracks, track]);

  if (!track) {
    return (
      <section className="pod pod-now">
        <Link to="/podcast" className="pod-back">Back to podcast</Link>
        <p className="pod-empty">Choose an episode to start listening.</p>
      </section>
    );
  }

  const nextDisabled = podcast.queue.length <= 1 || (!podcast.shuffle && podcast.queueIndex >= podcast.queue.length - 1);
  const transcript = plainText(track.lyrics);
  const description = plainText(track.description);

  const share = async () => {
    const url = `${window.location.origin}/podcast`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className={`pod pod-now ${podcast.videoMode ? "is-video" : ""}`}>
      <div className="pod-now__top">
        <Link to="/podcast" className="pod-back">Podcast</Link>
        <strong>Now playing</strong>
        <div className="pod-now__tools">
          <button type="button" onClick={() => setPanel(podcast.queue.length > 1 ? "queue" : "related")} aria-label="Queue">
            <Icon name="list" />
          </button>
          <button type="button" onClick={share} aria-label="Copy link">
            <Icon name="share alternate" />
          </button>
          <button type="button" className={saved ? "is-saved" : ""} onClick={() => podcast.toggleFavorite(track._id)} aria-label="Save episode">
            <Icon name={saved ? "heart" : "heart outline"} />
          </button>
        </div>
      </div>
      {copied && <p className="pod-note">Link copied</p>}

      <div className="pod-now__layout">
        <div className="pod-now__player">
          <div className="pod-stage" id="pod-stage">
            {podcast.videoMode ? null : <Cover track={track} />}
          </div>
          <h1>{track.title}</h1>
          <div className="pod-pills">
            {track.albumTitle && <span>{track.albumTitle}</span>}
            {track.mood && <span>{track.mood}</span>}
            <span>{track.listens || 0} plays</span>
            {myRating > 0 && <span>{myRating} star{myRating === 1 ? "" : "s"}</span>}
          </div>
          <div className="pod-stars" aria-label="Rate this episode">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                type="button"
                key={star}
                className={star <= (myRating || 0) ? "is-on" : ""}
                onClick={() => {
                  const result = podcast.rate(track._id, star);
                  setNotice(result.ok ? "Thanks for the rating." : result.message);
                }}
                aria-label={`${star} star`}
              >
                <Icon name={star <= (myRating || 0) ? "star" : "star outline"} />
              </button>
            ))}
          </div>
          {notice && <p className="pod-note">{notice}</p>}

          <label className="pod-seek">
            <input
              type="range"
              min="0"
              max={podcast.duration || 0}
              step="0.1"
              value={Math.min(podcast.position, podcast.duration || 0)}
              onChange={(event) => podcast.seek(Number(event.target.value))}
              aria-label="Seek"
            />
            <span>{formatClock(podcast.position)}</span>
            <span>{formatClock(podcast.duration)}</span>
          </label>

          <div className="pod-controls">
            <button type="button" onClick={podcast.playPrevious} disabled={podcast.queueIndex <= 0} aria-label="Previous">
              <Icon name="step backward" />
            </button>
            <button type="button" onClick={podcast.stop} aria-label="Stop">
              <Icon name="stop" />
            </button>
            <button type="button" className="is-play" onClick={() => podcast.toggle().catch(() => {})} aria-label={podcast.playing ? "Pause" : "Play"}>
              <Icon name={podcast.buffering ? "spinner" : podcast.playing ? "pause" : "play"} loading={podcast.buffering} />
            </button>
            <button type="button" className={saved ? "is-saved" : ""} onClick={() => podcast.toggleFavorite(track._id)} aria-label="Save">
              <Icon name={saved ? "heart" : "heart outline"} />
            </button>
            <button type="button" onClick={podcast.playNext} disabled={nextDisabled} aria-label="Next">
              <Icon name="step forward" />
            </button>
          </div>
          <div className="pod-modes">
            <button type="button" className={podcast.shuffle ? "is-on" : ""} onClick={podcast.toggleShuffle}>Shuffle</button>
            <button type="button" className={podcast.repeatOne ? "is-on" : ""} onClick={podcast.toggleRepeat}>Repeat</button>
            <button type="button" onClick={() => podcast.seek(Math.max(0, podcast.position - 15))}>−15s</button>
            <button type="button" onClick={podcast.cycleSpeed}>{podcast.speed}×</button>
            <button type="button" onClick={() => podcast.seek(podcast.position + 15)}>+15s</button>
          </div>
          {podcast.queue.length > 1 ? (
            <p className="pod-note">Up next {podcast.queueIndex + 1} / {podcast.queue.length}</p>
          ) : (
            <button type="button" className="pod-more" onClick={() => setPanel("related")}>
              {related.length ? `${related.length} similar episodes` : "Find similar episodes"}
            </button>
          )}
        </div>

        <div className="pod-copy">
          <h2>Transcript</h2>
          <p>{transcript || "Transcript coming soon."}</p>
          {description && (
            <>
              <h2>Description</h2>
              <p>{description}</p>
            </>
          )}
        </div>
      </div>

      {panel && (
        <div className="pod-sheet" onClick={() => setPanel("")}>
          <div className="pod-sheet__card" onClick={(event) => event.stopPropagation()}>
            <div className="pod-sheet__head">
              <h3>{panel === "queue" ? "Queue" : "Similar episodes"}</h3>
              <button type="button" onClick={() => setPanel("")} aria-label="Close"><Icon name="close" /></button>
            </div>
            <div className="pod-list">
              {(panel === "queue" ? podcast.queue : related).map((item, index) => (
                <button
                  type="button"
                  className={`pod-row ${panel === "queue" && index === podcast.queueIndex ? "is-current" : ""}`}
                  key={`${item._id}-${index}`}
                  onClick={() => {
                    setPanel("");
                    if (panel === "queue") podcast.playAt(index).catch(() => {});
                    else podcast.playQueue(related, index).catch(() => {});
                  }}
                >
                  <span className="pod-row__copy">
                    <strong>{item.title}</strong>
                    <small>{item.mood}</small>
                  </span>
                </button>
              ))}
              {panel === "related" && !related.length && <p className="pod-empty">No similar episodes yet</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
